/**
 * IndexedDB Wrapper fuer den Offline-Modus.
 *
 * Verwendet die `idb`-Bibliothek (typsicherer Wrapper um die native IndexedDB-API).
 * Fallback auf rohe IndexedDB-Implementierung, falls `idb` nicht verfuegbar ist.
 */

import { openDB, IDBPDatabase } from 'idb';
import type { Receipt, ShoppingList } from '../types';

// ---------------------------------------------------------------------------
// Schema-Typen
// ---------------------------------------------------------------------------

interface PendingUploadRecord {
  id: string;
  file: Blob;
  fileName: string;
  createdAt: number;
}

interface PendingShoppingChange {
  id: string;        // itemId
  listId: string;
  is_checked: boolean;
  changedAt: number;
}

interface CachedShoppingList {
  id: string;
  name: string;
  item_count: number;
  checked_count: number;
  is_active: boolean;
  household_id: string | null;
  created_at: string;
  updated_at: string;
}

interface CachedShoppingListItem {
  id: string;
  shopping_list_id: string;  // Feldname wie im API-Typ
  name: string;
  quantity: string | null;
  is_checked: boolean;
  added_by: string;
  created_at: string;
  updated_at: string;
}

interface OfflineDB {
  receipts: {
    key: string;
    value: Receipt;
    indexes: { 'by-date': string };
  };
  pendingUploads: {
    key: string;
    value: PendingUploadRecord;
  };
  shoppingLists: {
    key: string;
    value: CachedShoppingList;
  };
  shoppingListItems: {
    key: string;
    value: CachedShoppingListItem;
    indexes: { 'by-shopping-list': string };
  };
  pendingShoppingChanges: {
    key: string;
    value: PendingShoppingChange;
  };
}

// ---------------------------------------------------------------------------
// DB-Singleton
// ---------------------------------------------------------------------------

let dbPromise: Promise<IDBPDatabase<OfflineDB>> | null = null;

function getDb(): Promise<IDBPDatabase<OfflineDB>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDB>('kassenzettel-offline', 2, {
      upgrade(db, oldVersion) {
        // Version 1 — Receipts & Pending Uploads
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains('receipts')) {
            const receiptStore = db.createObjectStore('receipts', { keyPath: 'id' });
            receiptStore.createIndex('by-date', 'receipt_date');
          }
          if (!db.objectStoreNames.contains('pendingUploads')) {
            db.createObjectStore('pendingUploads', { keyPath: 'id' });
          }
        }

        // Version 2 — Shopping Lists
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains('shoppingLists')) {
            db.createObjectStore('shoppingLists', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('shoppingListItems')) {
            const itemStore = db.createObjectStore('shoppingListItems', { keyPath: 'id' });
            itemStore.createIndex('by-shopping-list', 'shopping_list_id');
          }
          if (!db.objectStoreNames.contains('pendingShoppingChanges')) {
            db.createObjectStore('pendingShoppingChanges', { keyPath: 'id' });
          }
        }
      },
    });
  }
  return dbPromise;
}

// ---------------------------------------------------------------------------
// Receipts-Cache
// ---------------------------------------------------------------------------

/**
 * Speichert eine Liste von Belegen im lokalen Cache.
 */
export async function cacheReceipts(receipts: Receipt[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('receipts', 'readwrite');
  await Promise.all([
    ...receipts.map((r) => tx.store.put(r)),
    tx.done,
  ]);
}

/**
 * Liefert alle gecachten Belege aus IndexedDB zurueck.
 */
export async function getCachedReceipts(): Promise<Receipt[]> {
  const db = await getDb();
  return db.getAll('receipts');
}

/**
 * Loescht den gesamten Beleg-Cache.
 */
export async function clearReceiptCache(): Promise<void> {
  const db = await getDb();
  await db.clear('receipts');
}

// ---------------------------------------------------------------------------
// Pending-Uploads
// ---------------------------------------------------------------------------

/**
 * Fuegt einen Upload zur Warteschlange hinzu, der ausgefuehrt wird,
 * sobald die Verbindung wiederhergestellt ist.
 */
export async function addPendingUpload(
  id: string,
  file: Blob,
  fileName: string
): Promise<void> {
  const db = await getDb();
  const record: PendingUploadRecord = {
    id,
    file,
    fileName,
    createdAt: Date.now(),
  };
  await db.put('pendingUploads', record);
}

/**
 * Gibt alle ausstehenden Uploads zurueck.
 */
export async function getPendingUploads(): Promise<PendingUploadRecord[]> {
  const db = await getDb();
  return db.getAll('pendingUploads');
}

/**
 * Entfernt einen Upload aus der Warteschlange (nach erfolgreichem Upload).
 */
export async function removePendingUpload(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('pendingUploads', id);
}

/**
 * Gibt die Anzahl ausstehender Uploads zurueck.
 */
export async function getPendingUploadCount(): Promise<number> {
  const db = await getDb();
  return db.count('pendingUploads');
}

// ---------------------------------------------------------------------------
// Shopping Lists Cache
// ---------------------------------------------------------------------------

/**
 * Speichert Listen und ihre Items im lokalen Cache.
 * Wird nach jedem erfolgreichen Online-Fetch aufgerufen.
 */
export async function cacheShoppingLists(lists: ShoppingList[]): Promise<void> {
  const db = await getDb();

  const listTx = db.transaction('shoppingLists', 'readwrite');
  await Promise.all([
    ...lists.map((l) =>
      listTx.store.put({
        id: l.id,
        name: l.name,
        item_count: l.item_count,
        checked_count: l.checked_count,
        is_active: l.is_active,
        household_id: l.household_id ?? null,
        created_at: l.created_at,
        updated_at: l.updated_at,
      })
    ),
    listTx.done,
  ]);

  // Items aller Listen cachen
  const itemTx = db.transaction('shoppingListItems', 'readwrite');
  const allItemPuts: Promise<unknown>[] = [];
  for (const list of lists) {
    if (list.items) {
      for (const item of list.items) {
        allItemPuts.push(
          itemTx.store.put({
            id: item.id,
            shopping_list_id: item.shopping_list_id,
            name: item.name,
            quantity: item.quantity ?? null,
            is_checked: item.is_checked,
            added_by: item.added_by,
            created_at: item.created_at,
            updated_at: item.updated_at,
          })
        );
      }
    }
  }
  allItemPuts.push(itemTx.done);
  await Promise.all(allItemPuts);
}

/**
 * Liest gecachte Listen inkl. Items aus IndexedDB.
 * Gibt ein Array von ShoppingList-Objekten zurueck (wie die API).
 */
export async function getCachedShoppingLists(): Promise<ShoppingList[]> {
  const db = await getDb();
  const lists = await db.getAll('shoppingLists');
  const result: ShoppingList[] = [];

  for (const l of lists) {
    const items = await db.getAllFromIndex('shoppingListItems', 'by-shopping-list', l.id);
    result.push({
      id: l.id,
      name: l.name,
      is_active: l.is_active,
      item_count: items.length,
      checked_count: items.filter((i) => i.is_checked).length,
      household_id: l.household_id ?? undefined,
      created_at: l.created_at,
      updated_at: l.updated_at,
      items: items.map((i) => ({
        id: i.id,
        shopping_list_id: i.shopping_list_id,
        name: i.name,
        quantity: i.quantity ?? undefined,
        is_checked: i.is_checked,
        added_by: i.added_by,
        created_at: i.created_at,
        updated_at: i.updated_at,
      })),
    });
  }

  return result;
}

/**
 * Markiert ein Item als checked/unchecked in IndexedDB (Offline-Aenderung).
 * Schreibt gleichzeitig eine PendingChange fuer den spaetere Sync.
 */
export async function toggleCachedItem(itemId: string, checked: boolean): Promise<void> {
  const db = await getDb();

  // Item in Cache aktualisieren
  const item = await db.get('shoppingListItems', itemId);
  if (item) {
    await db.put('shoppingListItems', { ...item, is_checked: checked });

    // Aenderung als pending vormerken
    const change: PendingShoppingChange = {
      id: itemId,
      listId: item.shopping_list_id,
      is_checked: checked,
      changedAt: Date.now(),
    };
    await db.put('pendingShoppingChanges', change);
  }
}

/**
 * Gibt alle ausstehenden Einkaufslisten-Aenderungen zurueck.
 */
export async function getCachedPendingChanges(): Promise<PendingShoppingChange[]> {
  const db = await getDb();
  return db.getAll('pendingShoppingChanges');
}

/**
 * Loescht alle pending Shopping-Aenderungen (nach erfolgreichem Sync).
 */
export async function clearPendingShoppingChanges(): Promise<void> {
  const db = await getDb();
  await db.clear('pendingShoppingChanges');
}
